// @flow

import React from 'react';
import styled from '@emotion/styled';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Responsive from 'app/components/responsive/Responsive';
import { firstNameInitial } from 'app/utils/displayUtils';
import PlayerAvatar from 'app/components/challenge/PlayerAvatar';
import Tooltip from 'app/components/commons/Tooltip';
import PlayerLabels from 'app/components/challenge/PlayerLabels';

type Props = {
  name: string,
  labels?: Array<Object>,
  warning: boolean,
  exposureWarning?: boolean,
  initials: string,
  image?: string,
  onSelectSettingsTab?: (tab: string) => void
};

const defaultProps = {
  labels: [],
  image: undefined,
  exposureWarning: false,
  onSelectSettingsTab: undefined
};

const WarningE = styled.button(({ theme }) => ({
  color: theme.color.warning,
  fontSize: theme.fontSize.default,
  fontWeight: theme.fontWeight.demiBold,
  paddingLeft: theme.spacing.tiny
}));

const Name = styled.span(({ theme }) => ({
  fontWeight: theme.fontWeight.demiBold
}));

const AvatarCell = styled.div({
  alignItems: 'center',
  display: 'flex',
  flexBasis: 'auto',
  flexGrow: 0,
  flexShrink: 0,
  position: 'relative'
});

const StyledPlayerAvatar = styled(PlayerAvatar)(({ theme }) => ({
  [theme.mediaQuery.mobileOnly]: {
    display: 'none'
  },
  [theme.mediaQuery.desktopOnly]: {
    display: 'none'
  },
  marginRight: theme.spacing.tiny
}));

const PlayerBar = ({
  name,
  image,
  initials,
  labels,
  warning,
  exposureWarning,
  onSelectSettingsTab
}: Props) => (
  <AvatarCell>
    <StyledPlayerAvatar fallbackText={initials} image={image} size="small" />
    <Name>
      <Responsive mobile>{firstNameInitial(name)}</Responsive>
      <Responsive desktopAndUp>{name}</Responsive>
    </Name>
    {labels.length > 0 && <PlayerLabels items={labels} />}
    {warning && (
      <Tooltip title="Player projection is unusually volatile. This is usually caused by a lack of games, but may also be that the player is extremely inconsistent. Your exposure to these players should be watched carefully.">
        <FontAwesomeIcon icon={faExclamationTriangle} color="orange" style={{ marginLeft: 4 }} />
      </Tooltip>
    )}
    {exposureWarning && <WarningE onClick={() => onSelectSettingsTab('exposure')}>E</WarningE>}
  </AvatarCell>
);

PlayerBar.defaultProps = defaultProps;

export default PlayerBar;
